import { Component, TrackByFunction, ViewChild, ViewChildren, ElementRef, Input, QueryList } from '@angular/core';
import { CheckboxWidget, CheckboxOption, CheckboxOptionValue } from './checkbox.widget';
import { JSONSchema } from '../types/json-schema';
import { isWidgetRequired } from './overrides';

type TagValue = CheckboxOptionValue;

type Tag = CheckboxOption;

@Component( {
  selector: 'sp-taglist-widget',
  templateUrl: './taglist.widget.html',
} )
export class TagListWidget extends CheckboxWidget {
  schema: JSONSchema;
  @ViewChild( 'tagWrapper' ) wrapper: ElementRef;
  @ViewChild( 'tagSearch' ) searchField: ElementRef;
  @ViewChildren( 'tagItem' ) tagElements: QueryList<ElementRef>;
  @ViewChild( 'tagList' ) listElement: ElementRef;
  _listSearch: string = '';
  placeholder: string = '';
  listOpen: boolean = false;
  listSelectedIndex: number = -1;
  listHasFocus: boolean = false;
  searchHasFocus: boolean = false;
  openDirection: 'up' | 'down' = 'down';
  maxHeight: string = 'none';

  ngAfterViewInit() {
    super.ngAfterViewInit();

    // Set placeholder from schema options
    this.placeholder = (
      this.schema.items &&
      !Array.isArray(this.schema.items) &&
      this.schema.items.options &&
      typeof this.schema.items.options.enumBlankTitle !== 'undefined'
    ) ? this.schema.items.options.enumBlankTitle : 'Please select';
  }

  get listSearch() {
    return this._listSearch;
  }

  set listSearch( search: string ) {
    if ( search.trim() === '' ) {
      this.listSelectedIndex = -1;
    } else if ( this.listSearch !== search ) {
      this.listSelectedIndex = 0;
    }
    this._listSearch = search;
  }

  tagIdentity( index: string, tag: Tag ): TagValue {
    return tag.value;
  }

  focusSearch() {
    ( <HTMLInputElement>this.searchField.nativeElement ).focus();
  }

  wrapperClick( $event: MouseEvent ) {
    if ( this.wrapper && $event.target === this.wrapper.nativeElement ) {
      this.focusSearch();
    }
  }

  tagKeydown( tag: Tag, $event: KeyboardEvent ) {
    if ( this.schema.readOnly ) return;

    const tagIndex = this.tagElements.toArray().findIndex( _tag => _tag.nativeElement === $event.target );
    switch ( $event.key ) {

      case 'ArrowLeft':
      case 'ArrowRight':
        const newSelection = tagIndex + ( $event.key === 'ArrowLeft' ? -1 : 1 );
        if ( newSelection === -1 || newSelection === this.tagElements.length ) {
          this.focusSearch();
        } else {
          this.focusTag( newSelection );
        }
        break;

      case 'Delete':
      case 'Backspace':
        this.checkToggle( tag );
        setTimeout( () => {
          if ( this.tagElements.length === 0 ) {
            this.focusSearch();
          } else {
            this.focusTag( tagIndex );
          }
        }, 0 );
        break;

      case 'Shift':
      case 'Control':
      case 'Meta':
      case 'Alt':
      case 'Tab':
        // Nothing
        break;

      default:
        // Redirect all typing to the search
        this.focusSearch();
        break;

    }
  }

  searchKeydown( $event: KeyboardEvent ) {
    const mod = ( this.searchResults.length + 1 );

    switch ( $event.key ) {

      case 'ArrowUp':
        if ( $event.shiftKey || $event.altKey || $event.ctrlKey || $event.metaKey ) break;
        $event.preventDefault();
        this.openList();
        this.listSelectedIndex = ( ( this.listSelectedIndex + mod + 0 ) % mod ) - 1;
        setTimeout(() => this.scrollListToActiveItem() );
        break;

      case 'ArrowDown':
        if ( $event.shiftKey || $event.altKey || $event.ctrlKey || $event.metaKey ) break;
        $event.preventDefault();
        this.openList();
        this.listSelectedIndex = ( ( this.listSelectedIndex + mod + 2 ) % mod ) - 1;
        setTimeout(() => this.scrollListToActiveItem() );
        break;

      case 'ArrowLeft':
      case 'ArrowRight':
        if ( $event.shiftKey || $event.altKey || $event.ctrlKey || $event.metaKey ) break;
        if ( this.tagElements.length === 0 ) break;
        const direction = $event.key === 'ArrowLeft' ? -1 : 1;
        const selectionRange = window.getSelection().getRangeAt( 0 );
        let cursorPos = ( direction === -1 ? selectionRange.startOffset : selectionRange.endOffset ) + direction;
        if ( cursorPos < 0 ) {
          this.focusTag( this.tagElements.length - 1 );
        } else if ( cursorPos > this.listSearch.length ) {
          this.focusTag( 0 );
        }
        break;

      case 'Escape':
        $event.preventDefault();
        if ( this.listSearch.length > 0 ) {
          this.listSearch = '';
        } else {
          this.closeList();
        }
        break;

      case 'Enter':
        $event.preventDefault();
        if ( this.listSelectedIndex !== -1 ) {
          this.checkToggle( this.searchResults[ this.listSelectedIndex ] );
          this.resetSearch();
        }
        break;

      case 'Backspace':
        if ( this.listSearch.length === 0 && this.selectedTags.length ) {
          this.removeTag( this.selectedTags.length - 1 );
        }
        break;

      default:
        this.openList();
        break;

    }
  }

  searchFocus( $event: Event ) {
    this.searchHasFocus = true;
    this.openList();
  }

  searchBlur( $event: Event ) {
    if ( this.listHasFocus ) return;
    this.searchHasFocus = false;
    this.maybeCloseList();
  }

  listFocus( $event: Event ) {
    this.listHasFocus = true;
    setTimeout(() => this.focusSearch() );
  }

  listBlur( $event: Event ) {
    this.listHasFocus = false;
    this.maybeCloseList();
  }

  resetSearch() {
    this.listSearch = '';
    this.listSelectedIndex = -1;
  }

  clickListOption( option: Tag ) {
    this.checkToggle( option );
    this.resetSearch();
    this.focusSearch();
  }

  openList() {
    if ( !this.listOpen ) {
      this.listSelectedIndex = -1;
    }
    this.listOpen = true;
  }

  maybeCloseList() {
    setTimeout(() => {
      if ( !this.searchHasFocus && !this.listHasFocus ) {
        this.closeList();
      }
    } );
  }

  closeList() {
    this.listOpen = false;
    this.listSelectedIndex = -1;
    this.listSearch = '';
  }

  focusTag( index: number ) {
    this.closeList();
    const elements = this.tagElements.toArray();
    if ( elements.length ) {
      index = Math.min( index, elements.length - 1 );
      elements[ index ].nativeElement.focus();
    }
  }

  removeTag( index: number ) {
    this.checkToggle( this.selectedTags[ index ] );
  }

  cleanPaste( $event: ClipboardEvent ) {
    $event.preventDefault();
    let content = $event.clipboardData.getData( 'text/plain' );
    content = content.replace( /\n/g, ' ' );
    document.execCommand( "insertHTML", false, content );
  }

  get selectedTags(): Array<Tag> {
    return Object.keys( this.checked )
      .map( value => this.options.find( option => option.value === value ) )
      // Hide exclusive options from taglist
      .filter( option => option && !this.exclusiveOptionValues.includes( option.value ) );
  }

  get availableTags(): Array<Tag> {
    return this.options.filter( option => !this.checked[ option.value ] && !this.isExclusive( option ) );
  }

  get searchResults(): Array<Tag> {
    return this.availableTags.filter( tag => {
      return tag.label.toLowerCase().search( this.listSearch.trim().toLowerCase() ) !== -1;
    } );
  }

  get required() {
    return isWidgetRequired( this );
  }

  get showPlaceholder(): boolean {
    return this.selectedTags.length === 0 && this.listSearch.trim() === '';
  }

  onScroll( e ) {
    const rect = ( this.searchField.nativeElement as HTMLInputElement ).getBoundingClientRect();
    const top = rect.top;
    const bottom = window.innerHeight - rect.bottom;
    if ( top < bottom ) {
      this.openDirection = 'down';
      this.maxHeight = ( bottom - 10 ) + 'px';
    } else {
      this.openDirection = 'up';
      this.maxHeight = ( top - 10 ) + 'px';
    }
  }

  scrollListToActiveItem() {
    const list = this.listElement.nativeElement as HTMLUListElement;

    const canScroll = list.clientHeight < list.scrollHeight;
    if ( !canScroll ) return;

    const item = list.querySelector( '.active' ) as HTMLLIElement;
    if ( !item ) return;

    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;

    if ( itemTop < list.scrollTop ) {
      list.scrollTop = itemTop;
      return;
    }

    if ( itemBottom > list.clientHeight + list.scrollTop ) {
      list.scrollTop = itemBottom - list.clientHeight;
      return;
    }
  }

  onScrollBound = this.onScroll.bind( this );

  ngOnInit() {
    window.addEventListener( 'scroll', this.onScrollBound, true );
  }

  ngOnDestroy() {
    window.removeEventListener( 'scroll', this.onScrollBound, true );
  }

}



// WEBPACK FOOTER //
// /var/www/schemas-forms/src/app/widgets/taglist.widget.ts